import axios from "axios";

import { isMockEnabled } from "../lib/mock";
import { KeywordAgent } from "./keyword-agent";

// Only the campaign endpoints are swapped: the customer list is shared
// with the conversion events flow, which has no mock behind it.
const campaignsResource = () =>
  isMockEnabled() ? "mock-campaigns" : "campaigns";

export interface GoogleAdsCustomer {
  id: string;
  name: string;
  // The manager this account is reached through; null when it's directly
  // accessible.
  "login-customer-id": string | null;
  "manager?": boolean;
}

export const loginCustomerParams = (loginCustomerId: string | null) =>
  loginCustomerId ? { "login-customer-id": loginCustomerId } : undefined;

export const getGoogleAdsCustomers = async (
  workspaceSlug: string,
): Promise<GoogleAdsCustomer[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/google-ads/customer-accounts`,
    )
  ).data;
};

export enum GoogleAdsConversionOrigin {
  APP,
  CALL_FROM_ADS,
  GOOGLE_HOSTED,
  STORE,
  UNKNOWN,
  UNSPECIFIED,
  WEBSITE,
  YOUTUBE_HOSTED,
}

export enum GoogleAdsConversionActionStatus {
  ENABLED,
  HIDDEN,
  REMOVED,
  UNKNOWN,
  UNSPECIFIED,
}

export enum GoogleAdsConversionActionType {
  AD_CALL,
  ANDROID_APP_PRE_REGISTRATION,
  ANDROID_INSTALLS_ALL_OTHER_APPS,
  CLICK_TO_CALL,
  FIREBASE_ANDROID_CUSTOM,
  FIREBASE_ANDROID_FIRST_OPEN,
  FIREBASE_ANDROID_IN_APP_PURCHASE,
  FIREBASE_IOS_CUSTOM,
  FIREBASE_IOS_FIRST_OPEN,
  FIREBASE_IOS_IN_APP_PURCHASE,
  FLOODLIGHT_ACTION,
  FLOODLIGHT_TRANSACTION,
  GOOGLE_ANALYTICS_4_CUSTOM,
  GOOGLE_ANALYTICS_4_PURCHASE,
  GOOGLE_HOSTED,
  GOOGLE_PLAY_DOWNLOAD,
  GOOGLE_PLAY_IN_APP_PURCHASE,
  LEAD_FORM_SUBMIT,
  SALESFORCE,
  SEARCH_ADS_360,
  SMART_CAMPAIGN_AD_CLICKS_TO_CALL,
  SMART_CAMPAIGN_MAP_CLICKS_TO_CALL,
  SMART_CAMPAIGN_MAP_DIRECTIONS,
  SMART_CAMPAIGN_TRACKED_CALLS,
  STORE_SALES,
  STORE_SALES_DIRECT_UPLOAD,
  STORE_VISITS,
  THIRD_PARTY_APP_ANALYTICS_ANDROID_CUSTOM,
  THIRD_PARTY_APP_ANALYTICS_ANDROID_FIRST_OPEN,
  THIRD_PARTY_APP_ANALYTICS_ANDROID_IN_APP_PURCHASE,
  THIRD_PARTY_APP_ANALYTICS_IOS_CUSTOM,
  THIRD_PARTY_APP_ANALYTICS_IOS_FIRST_OPEN,
  THIRD_PARTY_APP_ANALYTICS_IOS_IN_APP_PURCHASE,
  UNIVERSAL_ANALYTICS_GOAL,
  UNIVERSAL_ANALYTICS_TRANSACTION,
  UNKNOWN,
  UNSPECIFIED,
  UPLOAD_CALLS,
  UPLOAD_CLICKS,
  WEBPAGE,
  WEBPAGE_CODELESS,
  WEBSITE_CALL,
}

export enum GoogleAdsConversionActionCategory {
  ADD_TO_CART,
  BEGIN_CHECKOUT,
  BOOK_APPOINTMENT,
  CONTACT,
  CONVERTED_LEAD,
  DEFAULT,
  DOWNLOAD,
  ENGAGEMENT,
  GET_DIRECTIONS,
  IMPORTED_LEAD,
  OUTBOUND_CLICK,
  PAGE_VIEW,
  PHONE_CALL_LEAD,
  PURCHASE,
  QUALIFIED_LEAD,
  REQUEST_QUOTE,
  SIGNUP,
  STORE_SALE,
  STORE_VISIT,
  SUBMIT_LEAD_FORM,
  SUBSCRIBE_PAID,
  UNKNOWN,
  UNSPECIFIED,
}

export interface GoogleAdsConversionAction {
  id: string;
  name: string;
  origin: GoogleAdsConversionOrigin;
  status: GoogleAdsConversionActionStatus;
  type: GoogleAdsConversionActionType;
  category: GoogleAdsConversionActionCategory;
}

export const getGoogleAdsConversionActions = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
): Promise<GoogleAdsConversionAction[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/google-ads/customer-accounts/${customerId}/conversion-actions`,
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

// Note: the conversion-action enums above are declared without string
// values, so they are numeric and never equal what the API sends. New
// enums here give their values explicitly.
export enum GoogleAdsCampaignStatus {
  ENABLED = "ENABLED",
  PAUSED = "PAUSED",
  REMOVED = "REMOVED",
  UNKNOWN = "UNKNOWN",
  UNSPECIFIED = "UNSPECIFIED",
}

// Zeroed rather than null, so a row always has numbers to render. On a
// campaign row it is the sum across that campaign's ad groups.
export interface GoogleAdsAgentStats {
  "pending-review": number;
  excluded: number;
  analyzed: number;
  "estimated-monthly-saving": number;
}

// The bare campaign, which is what an ad group carries with it.
export interface GoogleAdsCampaign {
  id: number;
  name: string;
  status: GoogleAdsCampaignStatus;
  "advertising-channel-type": string;
  "currency-code": string;
}

// A campaign as the hub lists it. An agent belongs to an ad group, so a
// campaign row counts its covered ad groups rather than carrying one.
export interface GoogleAdsCampaignRow extends GoogleAdsCampaign {
  "ad-groups": {
    total: number;
    "with-agent": number;
    active: number;
  };
  stats: GoogleAdsAgentStats;
}

export interface GoogleAdsAdGroup {
  id: number;
  name: string;
  status: GoogleAdsCampaignStatus;
  campaign: GoogleAdsCampaign;
  agent: KeywordAgent | null;
  "criteria-reviewed?": boolean;
  stats: GoogleAdsAgentStats;
}

const campaignsURL = (workspaceSlug: string, customerId: string): string =>
  `/api/v1/workspaces/${workspaceSlug}/integrations/google-ads/customer-accounts/${customerId}/${campaignsResource()}`;

export const getGoogleAdsCampaigns = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
): Promise<GoogleAdsCampaignRow[]> => {
  return (
    await axios.get(campaignsURL(workspaceSlug, customerId), {
      params: loginCustomerParams(loginCustomerId),
    })
  ).data;
};

export const getGoogleAdsCampaign = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
): Promise<GoogleAdsCampaignRow> => {
  return (
    await axios.get(
      `${campaignsURL(workspaceSlug, customerId)}/${campaignId}`,
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

export const getGoogleAdsAdGroups = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
): Promise<GoogleAdsAdGroup[]> => {
  return (
    await axios.get(
      `${campaignsURL(workspaceSlug, customerId)}/${campaignId}/ad-groups`,
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

export const getGoogleAdsAdGroup = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
): Promise<GoogleAdsAdGroup> => {
  return (
    await axios.get(
      `${campaignsURL(workspaceSlug, customerId)}/${campaignId}/ad-groups/${adGroupId}`,
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};
